import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { segment, withQuery, type SaveVariables } from "@/api/query";
import { reportKeys } from "@/api/reports";

/* Mirrors api/src/pbo_invoicing/compliance/schemas.py. Fees are strings ("100.00"). */

export type ComplianceStatus = "done" | "overdue" | "due_soon" | "upcoming";
export type Recurrence = "none" | "annual";

export const RECURRENCES: { value: Recurrence; label: string }[] = [
  { value: "none", label: "One-off" },
  { value: "annual", label: "Every year" },
];

export type ComplianceInput = {
  name: string;
  form: string;
  provision: string;
  period_label: string;
  due_on: string;
  responsible: string;
  notes: string;
  /** Rupees per day of delay (s.403); null when the obligation has no daily fee. */
  daily_fee_inr: string | null;
  recurrence: Recurrence;
};

export type ComplianceItem = ComplianceInput & {
  id: string;
  done_on: string | null;
  filing_ref: string;
  status: ComplianceStatus;
  days_late: number;
  accrued_fee_inr: string | null;
  archived: boolean;
  version: number;
};

export type DoneInput = { done_on: string; filing_ref: string; create_next: boolean };
export type DoneResult = { item: ComplianceItem; next_item: ComplianceItem | null };

export const complianceKeys = {
  all: ["compliance"] as const,
  list: (includeArchived: boolean) => ["compliance", includeArchived] as const,
};

// --------------------------------------------------------------------------- requests

export function listCompliance(includeArchived: boolean): Promise<ComplianceItem[]> {
  return apiJson<ComplianceItem[]>(
    withQuery("/compliance", { include_archived: includeArchived }),
  );
}

export function saveCompliance({
  input,
  existing,
}: SaveVariables<ComplianceInput>): Promise<ComplianceItem> {
  return existing
    ? apiSend<ComplianceItem>(
        `/compliance/${segment(existing.id)}`,
        "PUT",
        input,
        existing.version,
      )
    : apiSend<ComplianceItem>("/compliance", "POST", input);
}

export function markDone(id: string, input: DoneInput, version: number): Promise<DoneResult> {
  return apiSend<DoneResult>(`/compliance/${segment(id)}/done`, "POST", input, version);
}

export function reopen(id: string, version: number): Promise<ComplianceItem> {
  return apiSend<ComplianceItem>(`/compliance/${segment(id)}/reopen`, "POST", undefined, version);
}

export function setArchived(
  id: string,
  archived: boolean,
  version: number,
): Promise<ComplianceItem> {
  const action = archived ? "archive" : "unarchive";
  return apiSend<ComplianceItem>(
    `/compliance/${segment(id)}/${action}`,
    "POST",
    undefined,
    version,
  );
}

// --------------------------------------------------------------------------- hooks

export function useCompliance(includeArchived = false) {
  return useQuery({
    queryKey: complianceKeys.list(includeArchived),
    queryFn: () => listCompliance(includeArchived),
  });
}

function useComplianceMutation<Variables, Result>(
  mutationFn: (variables: Variables) => Promise<Result>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: complianceKeys.all }),
        queryClient.invalidateQueries({ queryKey: reportKeys.dashboard }),
      ]),
  });
}

export function useSaveCompliance() {
  return useComplianceMutation(saveCompliance);
}

export function useMarkDone() {
  return useComplianceMutation(
    ({ id, input, version }: { id: string; input: DoneInput; version: number }) =>
      markDone(id, input, version),
  );
}

export function useReopen() {
  return useComplianceMutation(({ id, version }: { id: string; version: number }) =>
    reopen(id, version),
  );
}

export function useSetArchived() {
  return useComplianceMutation(
    ({ id, archived, version }: { id: string; archived: boolean; version: number }) =>
      setArchived(id, archived, version),
  );
}
